import { Theme } from '@vagabondinternet/fiber';
import type React from 'react';
import { createContext, useContext } from 'react';

import './tokens.scss';

function injectFont(id: string, url: string): void {
  if (typeof document !== 'undefined' && !document.getElementById(id)) {
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = url;
    document.head.appendChild(link);
  }
}

injectFont('wh-font-fredoka', 'https://fonts.googleapis.com/css2?family=Fredoka:wght@400;500;600;700&display=swap');
injectFont(
  'wh-font-fraunces',
  'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,600&display=swap',
);
injectFont('wh-font-inter', 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

/*
 * `color-scheme: only …` in tokens.scss keeps browser auto-darkening off the themes, but Dark Reader,
 * which Firefox iOS runs for its "Website Dark Mode", ignores it and re-tints each colour on its own,
 * leaving letters dark on dark. This meta is Dark Reader's opt-out; it drops its theme if the meta
 * arrives after it has started.
 */
if (typeof document !== 'undefined' && !document.querySelector('meta[name="darkreader-lock"]')) {
  const lock = document.createElement('meta');
  lock.name = 'darkreader-lock';
  document.head.appendChild(lock);
}

export type WordsHappenThemeName = 'meadow' | 'editorial' | 'mono' | 'noir';

export interface WordsHappenThemeProps {
  theme?: WordsHappenThemeName;
  children: React.ReactNode;
  className?: string;
}

const WordsHappenThemeContext = createContext<WordsHappenThemeName>('meadow');

export const useWordsHappenTheme = (): { theme: WordsHappenThemeName } => ({
  theme: useContext(WordsHappenThemeContext),
});

const FIBER_THEME_MAP: Record<WordsHappenThemeName, 'light' | 'dark'> = {
  meadow: 'light',
  editorial: 'light',
  mono: 'light',
  noir: 'dark',
};

export const WordsHappenTheme: React.FC<WordsHappenThemeProps> = ({ theme = 'meadow', children, className }) => (
  <WordsHappenThemeContext.Provider value={theme}>
    <div data-wh-theme={theme} className={className}>
      <Theme theme={FIBER_THEME_MAP[theme]}>{children}</Theme>
    </div>
  </WordsHappenThemeContext.Provider>
);
